import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { applyTax } from '../../utils/tax';
import {
  Page,
  Layout,
  Card,
  Form,
  TextField,
  Select,
  Button,
  Toast,
  Text,
  Banner,
  Spinner,
  FormLayout,
  Icon,
  Badge,
  Avatar,
  ResourceList,
  ResourceItem,
  Checkbox,
  BlockStack,
  Box,
  InlineGrid,
  InlineStack,
  Pagination,
  Frame,
  useBreakpoints
} from '@shopify/polaris';
import {
  CheckSmallIcon,
  XIcon,BankIcon,PaymentIcon,EmailIcon    
} from "@shopify/polaris-icons";
import useAxiosClient from '../axiosClient';
import { useNavigate, useParams } from 'react-router-dom';
import { ProfileIcon, ClockIcon, MoneyIcon, CashDollarIcon, CalendarIcon, WalletIcon } from '@shopify/polaris-icons';
import { IconDisc } from '../ui/CardSurface';
import { DateRangePicker } from '../DateRangePicker';

/**
 * The hand-drawn card surface the admin's data pages use. Kept local because
 * ui/CardSurface here only exports TINT / IconDisc / ColumnHeading.
 */
const SURFACE = {
  background: '#ffffff',
  border: '1px solid #e3e3e3',
  borderRadius: '12px',
  boxShadow: '0 1px 0 rgba(0, 0, 0, 0.05)',
  overflow: 'hidden',
};

/** Card header band: tinted icon disc + title, on a hairline rule. */
function CardHeader({ icon, tone = 'info', title }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '10px 16px', borderBottom: '1px solid #ebebeb',
      }}
    >
      <IconDisc icon={icon} tone={tone} size={26} />
      <Text as="h3" variant="headingSm">{title}</Text>
    </div>
  );
}

export default function CreatePayroll() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const axiosClient = useAxiosClient();
  const { currencyData = {} } = window.appGlobals || {};
  const [showErrorBanner, setShowErrorBanner] = useState(true);
  // Add these state variables at the beginning of your component
const [currentPage, setCurrentPage] = useState(1);
const [itemsPerPage] = useState(5); // 5 items per page
const [error,setError]=useState();
  // Initial state: Payment type is set to 'salary' by default
  const [payrollData, setPayrollData] = useState({
    payment_method: '',
    payment_type: 'salary',
    notes: '',
    amount: '',
    unpaidSalary: '',
    unpaidCommission: '',
    dates: {},
    payment_proof: null,
    bonus_details: '',
  });
  const [staff, setStaff] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastContent, setToastContent] = useState('');
  const [selectedCommissions, setSelectedCommissions] = useState([]);
  const [unpaidCommissions, setUnpaidCommissions] = useState([]);
  const fetchUnpaidCommissions = async () => {
    try {
      const response = await axiosClient.get(`/getUnpaidCommissions/${id}`);
      if (response.data.success) {
        setUnpaidCommissions(response.data.commissions);
        // By default select all commissions
        setSelectedCommissions(response.data.commissions.map(c => c.id));
      }
    } catch (err) {
      // Error handled silently
    }
  };

  // Add useEffect to fetch commissions when payment type changes to commission
  useEffect(() => {
    if (payrollData.payment_type === 'commission') {
      fetchUnpaidCommissions();
    }
  }, [payrollData.payment_type]);


  const originalData = useRef(payrollData);
  const valueRef = useRef(payrollData);

  // Update valueRef whenever payrollData changes
  useEffect(() => {
    valueRef.current = payrollData;
  }, [payrollData]);

  // Helper: Format a Date object as YYYY-MM-DD
  const formatDateForInput = (date) => {
    if (!date) return '';
    const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    return utcDate.toISOString().split('T')[0];
  };

  // Helper: Format a date string for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  /** Midnight-UTC copy of a date, for boundary comparison only. */
  const toUtcDay = (value) => {
    const parsed = value instanceof Date ? value : new Date(value);
    if (!parsed || isNaN(parsed.getTime())) return null;
    return new Date(Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()));
  };

  const formatPeriodDate = (value) => {
    const parsed = value instanceof Date ? value : new Date(value);
    if (!parsed || isNaN(parsed.getTime())) return '';
    return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  /**
   * Clamp a requested range into the period this staff member can actually be
   * paid for.
   *
   * staff.startDate / staff.endDate are the backend's computed UNPAID period
   * (getUnpaidPeriodDates). Earlier dates have already been paid - paying them
   * again would double-pay - and later dates have not been earned yet.
   *
   * This previously REJECTED any range that crossed either boundary, so picking a
   * whole calendar month always failed whenever the unpaid period began mid-month:
   * the merchant got "Cannot select dates before the start date", was not told
   * what the allowed period was, and had no way to proceed. Clamping keeps the
   * double-payment protection while letting the selection succeed.
   *
   * Boundaries are compared at midnight UTC, but the returned dates are the
   * ORIGINAL local Date objects so downstream amount calculation and formatting
   * behave exactly as before.
   */
  const clampToPayableRange = (start, end) => {
    if (!staff) return null;

    const startUtc = toUtcDay(start);
    const endUtc = toUtcDay(end);
    if (!startUtc || !endUtc) return null;

    const minUtc = toUtcDay(staff.startDate);
    const maxUtc = toUtcDay(staff.endDate);

    // Entirely outside the window in one direction or the other - there is no
    // overlap to clamp to. The two directions are DIFFERENT problems and must not
    // share a message: a range ending before the window has already been paid,
    // while a range starting after it has not been worked yet. Telling a merchant
    // that September "is already paid" while September has not happened is worse
    // than saying nothing.
    if (minUtc && endUtc < minUtc) {
      return { outside: true, reason: 'paid', minDate: minUtc, maxDate: maxUtc };
    }
    if (maxUtc && startUtc > maxUtc) {
      return { outside: true, reason: 'future', minDate: minUtc, maxDate: maxUtc };
    }

    const clampStart = minUtc && startUtc < minUtc;
    const clampEnd = maxUtc && endUtc > maxUtc;

    const resolvedStart = clampStart ? new Date(staff.startDate) : new Date(start);
    const resolvedEnd = clampEnd ? new Date(staff.endDate) : new Date(end);

    // Belt and braces: an inverted or zero-width window would still land here.
    if (toUtcDay(resolvedStart) > toUtcDay(resolvedEnd)) {
      return { outside: true, reason: 'paid', minDate: minUtc, maxDate: maxUtc };
    }

    return {
      outside: false,
      start: resolvedStart,
      end: resolvedEnd,
      adjusted: Boolean(clampStart || clampEnd),
      minDate: minUtc,
      maxDate: maxUtc,
    };
  };

  // Salary: update date range and recalculate amount
  const handleDateRangeSelect = ({ start, end }) => {
    const payable = clampToPayableRange(start, end);
    if (!payable) return;

    if (payable.outside) {
      const message = t(
        payable.reason === 'future' ? 'period_not_yet_worked' : 'period_outside_payable_range',
        {
          start: formatPeriodDate(payable.minDate),
          end: formatPeriodDate(payable.maxDate),
        },
      );
      handleError(message);
      shopify.toast.show(message);
      return;
    }

    const startDate = payable.start;
    const endDate = payable.end;

    if (payable.adjusted) {
      shopify.toast.show(t('period_adjusted_to_payable_range', {
        start: formatPeriodDate(startDate),
        end: formatPeriodDate(endDate),
      }));
    }

    setPayrollData(prev => {
      const newData = {
        ...prev,
        dates: { start: startDate, end: endDate },
        start_date: formatDateForInput(startDate),
        end_date: formatDateForInput(endDate)
      };

      if (startDate && endDate && staff) {
        const newAmount = calculateAmountForDateRange(startDate, endDate, staff);
        newData.unpaidSalary = newAmount.toFixed(2);
        newData.amount = newAmount.toFixed(2);
      }
      return newData;
    });
    setIsDirty(true);
    setError('');
  };
  const safeToFixed = (value, decimals = 2) => {
    // Check if value is a number or can be converted to one
    const num = Number(value);
    return isNaN(num) ? '0.00' : num.toFixed(decimals);
  };
  // Only used for salary payments.
  const calculateAmountForDateRange = (startDate, endDate, staff) => {
    if (!startDate || !endDate || !staff) return 0;
    
    const start = new Date(Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()));
    const end = new Date(Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()));
    
    // For Hourly rate only - use actual hours worked
    if (staff.payroll_type === 'Hourly') {
      // Get the actual hours worked for the selected date range
      const selectedDays = staff.clockRecords.filter(record => {
        const recordDate = new Date(record.current_date);
        return recordDate >= start && recordDate <= end;
      });
  
      // Calculate total hours for the selected period
      const totalHours = selectedDays.reduce((sum, record) => {
        // Convert HH:MM:SS to decimal hours
        if (!record.total_time_worked) return sum;
        const [hours, minutes, seconds] = record.total_time_worked.split(':').map(Number);
        const decimalHours = hours + (minutes / 60) + (seconds / 3600);
        return sum + decimalHours;
      }, 0);
  
      // Calculate amount based on hourly rate and actual hours worked
      const amount = totalHours * parseFloat(staff.salary_amount);
      return Math.min(parseFloat(amount.toFixed(2)), parseFloat(staff.unpaidSalary));
    } 
    
    // For Weekly and Monthly calculations
    const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    let amount = 0;
  
    switch (staff.payroll_type) {
      case 'Weekly': {
        const weeks = Math.ceil(daysDiff / 7);
        amount = weeks * parseFloat(staff.salary_amount);
        break;
      }
      case 'Monthly': {
        const monthlyRate = parseFloat(staff.salary_amount);
        const averageDaysInMonth = 30.44;
        amount = (daysDiff / averageDaysInMonth) * monthlyRate;
        break;
      }
      default:
        amount = 0;
    }
    
    return Math.min(parseFloat(amount.toFixed(2)), parseFloat(staff.unpaidSalary));
  };

  // Fetch staff data
  useEffect(() => {
    fetchStaffData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);



  const fetchStaffData = async () => {
    try {
      setIsLoading(true);
      const response = await axiosClient.get(`/getStaffWithPayment/${id}`);
      if (response.data.success) {
        const staffData = response.data.staff;
        setStaff(staffData);
        const startDate = new Date(staffData.startDate);
        const endDate = new Date(staffData.endDate);
        setPayrollData(prev => ({
          ...prev,
          payment_method: staffData.payment_method || '',
          // Rounded at the SOURCE, not at render time. The field is a controlled
          // input, so formatting it on every render would fight the merchant as
          // they type ("24.3" would snap to "24.30" mid-keystroke). These values
          // come off the API as raw floats - .toString() on 24.3 can yield
          // "24.299999999999997" - so they are normalised once, here.
          amount: safeToFixed(staffData.unpaidAmount),
          unpaidSalary: safeToFixed(staffData.unpaidSalary),
          unpaidCommission: safeToFixed(staffData.unpaidCommission),
          dates: { start: startDate, end: endDate },
          start_date: formatDateForInput(startDate),
          end_date: formatDateForInput(endDate)
        }));
        originalData.current = payrollData;
      }
    } catch (err) {
      handleError(t('error_fetching_staff_data'));
    } finally {
      setIsLoading(false);
    }
  };
  const handleCommissionSelection = (id) => {
    setSelectedCommissions(prev => {
      if (prev.includes(id)) {
        return prev.filter(commId => commId !== id);
      } else {
        return [...prev, id];
      }
    });
  };
  // Save handler
  /**
   * Gross / tax / net for the amount ABOUT to be committed.
   *
   * SALARY ONLY, matching createPayroll(): bonuses, commission payouts and tips
   * are paid in full and are never taxed, so this is deliberately not rendered on
   * those branches. If tax is ever extended to another payment type, the backend
   * gate in createPayroll() and this preview must change together - showing a
   * breakdown the backend does not apply (or vice versa) is worse than neither.
   *
   * Preview only: the backend recomputes and stores the authoritative split.
   */
  const renderTaxPreview = (grossValue) => {
    const preview = applyTax(
      staff?.tax_type,
      staff?.tax_amount ?? staff?.tax_rate,
      grossValue || 0,
    );
    if (!preview.taxType) return null;
    const sym = currencyData[staff?.currency]?.symbol || '$';
    return (
      <Box paddingBlockStart="300">
        <Card background="bg-surface-secondary">
          <BlockStack gap="100">
            <InlineStack align="space-between">
              <Text variant="bodySm" tone="subdued">{t('gross_earnings')}</Text>
              <Text variant="bodySm">{sym}{preview.gross.toFixed(2)}</Text>
            </InlineStack>
            <InlineStack align="space-between">
              <Text variant="bodySm" tone="subdued">
                {t('tax_deduction')}
                {preview.taxType === 'percentage' ? ` (${preview.taxRate}%)` : ''}
              </Text>
              <Text variant="bodySm" tone="critical">-{sym}{preview.tax.toFixed(2)}</Text>
            </InlineStack>
            <InlineStack align="space-between">
              <Text variant="bodyMd" fontWeight="bold">{t('net_pay')}</Text>
              <Text variant="bodyMd" fontWeight="bold">{sym}{preview.net.toFixed(2)}</Text>
            </InlineStack>
          </BlockStack>
        </Card>
      </Box>
    );
  };

  /**
   * Clamp and format the salary amount once the merchant finishes typing.
   *
   * Deliberately on blur, not on change - see the comment in handleChange.
   */
  const handleAmountBlur = () => {
    setPayrollData((prev) => {
      if (prev.payment_type !== 'salary') return prev;

      const numValue = parseFloat(prev.amount);
      if (isNaN(numValue) || numValue < 0) return { ...prev, amount: '' };

      const ceiling = parseFloat(staff?.unpaidSalary);
      const clamped = isNaN(ceiling) ? numValue : Math.min(numValue, ceiling);

      return { ...prev, amount: clamped.toFixed(2) };
    });
  };

  const handleSave = async () => {
    if (!validateForm()) return;
  
    try {
      setIsSaving(true);
      setError('');
  
      // Create FormData object
      const formData = new FormData();
  
      // Add all the fields to FormData
      formData.append('staff_id', staff.id);
      formData.append('payment_method', payrollData.payment_method);
      formData.append('payment_type', payrollData.payment_type);
      formData.append('amount', payrollData.amount);
      formData.append('unpaidSalary', payrollData.unpaidSalary);
      formData.append('notes', payrollData.notes || '');
  
      // Add payment proof if exists
      if (payrollData.payment_proof instanceof File) {
        formData.append('payment_proof', payrollData.payment_proof);
      }
  
      // Add bonus details if it's a bonus payment
      if (payrollData.payment_type === 'bonus') {
        formData.append('bonus_details', payrollData.bonus_details);
      }
  
      // Add commission orders if it's a commission payment
      if (payrollData.payment_type === 'commission') {
        // Convert array to JSON string
        formData.append('commission_orders', JSON.stringify(selectedCommissions));
      }
  
      // Add dates if it's a salary payment
      if (payrollData.payment_type === 'salary') {
        formData.append('start_date', payrollData.start_date);
        formData.append('end_date', payrollData.end_date);
      }
  
      const response = await axiosClient.post('/createPayment', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
  
      if (response.data.success) {
        originalData.current = payrollData;
        setIsDirty(false);
        navigate('/Payroll');
      }else{
        setError(response.data.message);
      }
    } catch (err) {
      handleError('Failed to process payment:'+err);
      setToastContent('Error processing payment');
    } finally {
      setIsSaving(false);
      setShowToast(true);
    }
  };

  // Discard handler
  const handleDiscard = () => {
    fetchStaffData();
    setIsDirty(false);
  };

  // Validate form fields (adjusted to check for bonus details)
  const validateForm = () => {
    const errors = [];
    if (!payrollData.payment_type) {
      errors.push('Payment type is required');
    }
    if (!payrollData.payment_method) {
      errors.push('Payment method is required');
    }

    // Validate based on payment type
    if (payrollData.payment_type === 'salary') {
      if (!payrollData.dates.start || !payrollData.dates.end) {
        errors.push('Both start and end dates are required for salary payments');
      } else if (payrollData.dates.end < payrollData.dates.start) {
        errors.push('End date cannot be before start date');
      }
    } else if (payrollData.payment_type === 'bonus') {
      if (!payrollData.bonus_details) {
        errors.push('Please provide a reason for the bonus');
      }
    }

    const amount = parseFloat(payrollData.amount);
    if (!payrollData.amount || isNaN(amount) || amount <= 0) {
      errors.push('Please enter a valid amount');
    }

    if (errors.length > 0) {
      setError(errors.join('. '));
      return false;
    }
    setError('');
    return true;
  };

  const handleChange = (field) => (value) => {
    setPayrollData((prev) => {
      const newData = { ...prev, [field]: value };

      // When payment type changes:
      if (field === 'payment_type') {
        if (value === 'salary') {
          // For salary, initialize with staff defaults
          if (staff) {
            const startDate = new Date(staff.startDate);
            const endDate = new Date(staff.endDate);
            newData.dates = { start: startDate, end: endDate };
            newData.start_date = formatDateForInput(startDate);
            newData.end_date = formatDateForInput(endDate);
            const newAmount = calculateAmountForDateRange(startDate, endDate, staff);
            newData.amount = newAmount.toFixed(2);
          }
        } else if (value === 'commission') {
          // For commission, reset dates and let the useEffect handle the amount
          newData.dates = {};
          newData.start_date = '';
          newData.end_date = '';
          // Don't set amount here, let the useEffect handle it
        } else if (value === 'bonus') {
          // For bonus, reset dates and amount
          newData.dates = {};
          newData.start_date = '';
          newData.end_date = '';
          newData.amount = '';
          newData.bonus_details = '';
        } else {
          // For other types, clear dates and amount
          newData.dates = {};
          newData.start_date = '';
          newData.end_date = '';
          newData.amount = '';
        }
      }

      // Handle amount changes for non-commission payment types
      if (field === 'amount' && prev.payment_type !== 'commission') {
        if (prev.payment_type === 'salary') {
          // Keep the RAW keystroke. Rounding here made the field impossible to
          // type into: parseFloat(...).toFixed(2) rewrites "2" to "2.00" on the
          // first character, so the merchant can never reach "24.30". Clamping
          // to the unpaid balance and formatting to 2dp happen on blur instead
          // (handleAmountBlur), by which point they have finished typing.
          newData.amount = value;
        } else if (prev.payment_type === 'bonus') {
          // For bonus, use the raw value without formatting
          newData.amount = value;
        } else {
          // For other types
          const numValue = parseFloat(value);
          if (!isNaN(numValue)) {
            newData.amount = numValue.toFixed(2);
          } else {
            newData.amount = '';
          }
        }
      }

      return newData;
    });
    setIsDirty(true);
  };

  // Helper functions to format values
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '';
    const numAmount = parseFloat(amount);
    const currencySymbol = currencyData[staff?.currency]?.symbol || '$';
    return `${currencySymbol}${isNaN(numAmount) ? '0.00' : numAmount.toFixed(2)}`;
  };
  
  const formatHours = (hours) => {
    if (hours === undefined || hours === null) return '0.00h';
    const numHours = parseFloat(hours);
    return `${isNaN(numHours) ? '0.00' : numHours.toFixed(2)}h`;
  };

  // Modify this useEffect
  useEffect(() => {
    if (payrollData.payment_type === 'commission' && unpaidCommissions.length > 0) {
      const totalCommission = selectedCommissions.reduce((sum, id) => {
        const commission = unpaidCommissions.find(c => c.id === id);
        return commission ? sum + parseFloat(commission.commission_earned) : sum;
      }, 0);

      // Update only the amount field
      setPayrollData(prev => ({
        ...prev,
        amount: totalCommission.toFixed(2)
      }));
    }
  }, [selectedCommissions, payrollData.payment_type, unpaidCommissions]);
  // Loading state
  if (isLoading) {
    return (
      <Page>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Spinner accessibilityLabel={t("loading_staff_data")} size="large" />
        </div>
      </Page>
    );
  }

  // Error state if staff data is not found
  if (!staff) {
    return (
      <Page>
        <Banner status="critical" title={t("error_title")}>
          <p>Staff member not found</p>
        </Banner>
      </Page>
    );
  }

  const isPaymentDetailsComplete = (staffData, paymentMethod) => {
    if (!staffData || !paymentMethod) return false;

    switch (paymentMethod) {
      case 'PayPal':
        return staffData.payment_email && staffData.paypal_name;

      case 'Stripe':
        return staffData.stripe_email && staffData.stripe_account_id;

      case 'Wise':
        return staffData.wise_email && staffData.wise_account_name && staffData.wise_currency;

      case 'Bank Transfer':
        if (staffData.bank_account_type === 'domestic') {
          return staffData.bank_name &&
            staffData.account_holder_name &&
            staffData.account_number &&
            staffData.routing_number;
        } else {
          return staffData.bank_name &&
            staffData.account_holder_name &&
            staffData.account_number &&
            staffData.swift_code &&
            staffData.iban;
        }

      case 'Direct Deposit':
        return staffData.bank_name &&
          staffData.account_holder_name &&
          staffData.account_number &&
          staffData.routing_number;

      case 'Revolut':
        return staffData.revolut_contact && staffData.revolut_username;

      case 'Venmo':
        return staffData.venmo_username && staffData.venmo_phone;

      case 'Square':
        return staffData.cash_tag && staffData.cash_app_contact;

      case 'Cash':
      case 'Check':
        return true; // These methods don't require additional details

      default:
        return false;
    }
  };
  const handleError = (errorMessage) => {
    if (errorMessage) {
      setShowErrorBanner(true);
    }
    setError(errorMessage);
  };
  const maskAccountNumber = (number) => {
    if (!number) return 'Not set';
    // Convert to string if it's a number
    const numberString = number.toString();
    if (numberString.length <= 4) return numberString;
    const visible = numberString.slice(-4);
    return '••••' + visible;
  };

//     const handleBackClick = useCallback(() => {
//   if (isDirty) {
//     // Check if appBridge and saveBar exist before using
//     if (appBridge && appBridge.saveBar) {
//       appBridge.saveBar.leaveConfirmation()
//         .then(() => {
//           navigate("/Payroll");
//         })
//         .catch(() => {
//           console.log("Navigation cancelled");
//         });
//     } else {
//       // Fallback if saveBar is not available
//       const confirmLeave = window.confirm("You have unsaved changes. Are you sure you want to leave?");
//       if (confirmLeave) {
//         navigate("/Payroll");
//       }
//     }
//   } else {
//     navigate("/Payroll");
//   }
// }, [isDirty, appBridge, navigate]);
const handleBackClick = () => {
  navigate("/Payroll");
}
  
  return (
    <>
      {/* Add CSS for spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      {/* Shopify-style Save Bar at top */}
      {isDirty && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: '#1a1a1a',
          color: 'white',
          padding: '12px 20px',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <Text variant="bodyMd" as="span" color="inverse">
            {t('unsaved_changes')}
          </Text>
          <InlineStack gap="200">
            <button
              onClick={handleDiscard}
              disabled={isSaving}
              style={{
                backgroundColor: 'transparent',
                border: '1px solid #ffffff',
                color: '#ffffff',
                padding: '10px 16px',
                borderRadius: '6px',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: isSaving ? 0.6 : 1,
                height: '36px',
                minWidth: '80px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {t('discard')}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              style={{
                backgroundColor: '#008060',
                border: '1px solid #008060',
                color: '#ffffff',
                padding: '10px 16px',
                borderRadius: '6px',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                opacity: isSaving ? 0.6 : 1,
                height: '36px',
                minWidth: '80px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {isSaving ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    width: '12px',
                    height: '12px',
                    border: '2px solid #ffffff',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></span>
                  {t('save')}
                </span>
              ) : (
                t('save')
              )}
            </button>
          </InlineStack>
        </div>
      )}
      
      {/* Polaris <Page> ignores an arbitrary `style` prop, so the fixed save bar above
          was covering the page title and back action whenever the form was dirty.
          A real spacer sibling is what actually pushes the page down. */}
      {isDirty && <div style={{ height: '60px' }} aria-hidden="true" />}
      <Page
        title={`${t("create_payroll")} - ${staff.first_name} ${staff.last_name}`}
        fullWidth
        backAction={{
          content: t("payroll_title"),
          onAction: handleBackClick, // Use the new handler
        }}
      >
      <Form onSubmit={handleSave}>
        <Layout>
        {error && showErrorBanner && (
  <Layout.Section>
    <Banner 
              tone="critical"
        title={t("error_title")} 
      onDismiss={() => setShowErrorBanner(false)}
    >
      <p>{error}</p>
    </Banner>
  </Layout.Section>
)}

          {/* Staff Payment Overview */}
          <Layout.AnnotatedSection
            title={t("staff_payment_overview_title")}
            description={t("review_staff_member_payment_details_and_history")}
          >
            <div style={{ ...SURFACE, overflow: 'visible' }}>
              {/* Who this payroll is for. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', borderBottom: '1px solid #ebebeb' }}>
                <Avatar
                  name={`${staff.first_name} ${staff.last_name}`}
                  initials={`${staff.first_name?.charAt(0) || ''}${staff.last_name?.charAt(0) || ''}`.toUpperCase()}
                />
                <div style={{ minWidth: 0 }}>
                  <Text as="h2" variant="headingMd">
                    {staff.first_name} {staff.last_name}
                  </Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px', flexWrap: 'wrap' }}>
                    <Text as="span" variant="bodySm" tone="subdued">{staff.email}</Text>
                    <Badge tone={staff.payroll_type === 'Hourly' ? 'success' : 'info'}>
                      {staff.payroll_type}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="p-4">
                {/* The three headline figures, on one hairline strip. */}
                <div className="grid grid-cols-1 sm:grid-cols-3" style={{ gap: '1px', background: '#ebebeb', border: '1px solid #ebebeb', borderRadius: '10px', overflow: 'hidden' }}>
                  <div style={{ background: '#ffffff', padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <IconDisc icon={MoneyIcon} tone="info" size={26} />
                      <Text as="span" variant="bodySm" tone="subdued">{t("rate")}</Text>
                    </div>
                    <div style={{ marginTop: '8px', fontSize: '20px', fontWeight: 700, color: '#303030', letterSpacing: '-0.3px' }}>
                      {formatCurrency(staff.salary_amount)}
                      <span style={{ fontSize: '13px', fontWeight: 400, color: '#616161' }}>
                        {staff.payroll_type === 'Hourly' ? '/hour' : staff.payroll_type === 'Weekly' ? '/week' : '/month'}
                      </span>
                    </div>
                  </div>
                  <div style={{ background: '#ffffff', padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <IconDisc icon={ClockIcon} tone="info" size={26} />
                      <Text as="span" variant="bodySm" tone="subdued">{t("hours_worked")}</Text>
                    </div>
                    <div style={{ marginTop: '8px', fontSize: '20px', fontWeight: 700, color: '#303030', letterSpacing: '-0.3px' }}>
                      {staff.totalHours}
                    </div>
                  </div>
                  {/* This is the figure the whole page is about, so it carries the
                      accent - Polaris bg-surface-warning, not Tailwind's yellow-100. */}
                  <div style={{ background: '#fff1e3', padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <IconDisc icon={WalletIcon} tone="warning" size={26} />
                      <Text as="span" variant="bodySm" tone="subdued">{t("remaining_balance")}</Text>
                    </div>
                    <div style={{ marginTop: '8px', fontSize: '20px', fontWeight: 700, color: '#5e4200', letterSpacing: '-0.3px' }}>
                      {formatCurrency(staff.totalRemaining)}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                    <h3 className="text-md font-medium mb-4">{t("payment_history")}</h3>
                    <div className="flex justify-between mb-3">
                      <div>
                        <p className="text-sm text-gray-500">{t("total_earnings")}</p>
                        <p className="text-lg font-semibold text-green-600">
                          {formatCurrency(staff.totalEarnings)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">{t("total_paid")}</p>
                        <p className="text-lg font-semibold text-blue-600">
                          {formatCurrency(staff.totalPaid)}
                        </p>
                      </div>
                    </div>
                  </div>


                  <div className="p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                    <h3 className="text-md font-medium mb-4">{t("payment_schedule")}</h3>
                    <div className="space-y-4">
                      {/* Last Paid Period */}
                      {staff.lastPaidPeriod && staff.lastPaidPeriod.start_date != null && staff.lastPaidPeriod.end_date != null && (
                        <div className="p-3 bg-gray-50 rounded-md">
                          <div className="flex justify-between items-center mb-2">
                            <p className="text-sm text-gray-500">{t("last_payment")}</p>
                            <Badge tone="success" progress="complete">
                              Paid
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <div style={{margin:'0px'}}>
                            <Icon source={ClockIcon} tone="base" />
                            </div>
                            <p className="text-sm">
                              {staff.lastPaidPeriod.start_date != null && staff.lastPaidPeriod.end_date != null && (
                                <>
                                  {formatDate(staff.lastPaidPeriod.start_date)} -{' '}
                                  {formatDate(staff.lastPaidPeriod.end_date)}
                                </>
                              )}
                            </p>
                          </div>
                        </div>
                      )}

                      {staff.paidPeriods && staff.paidPeriods.start_date === null && staff.paidPeriods.end_date == null && staff.paidPeriods.amount && (
                        <div className="p-3 bg-gray-50 rounded-md">
                          <div className="flex justify-between items-center mb-2">
                            <p className="text-sm text-gray-500">{t("last_commission_payment")}</p>
                            <Badge status="success" progress="complete">
                              Paid
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <Icon source={ClockIcon} color="base" />
                            <p className="text-sm">
                              {staff.paidPeriods.amount}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Unpaid Salary Period */}
                      {staff.unpaidSalary > 0 && (
                        <div className="p-3 bg-gray-50 rounded-md">
                          <div className="flex justify-between items-center mb-1">
                            <p className="text-sm text-gray-500">{t("unpaid_salary_period")}</p>
                            <Badge tone="warning">{t("pending_status")}</Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <div style={{ margin: '0px' }}>
                              <Icon source={ClockIcon} tone="base" />
                            </div>
                            <p className="text-sm">
                              Starts {formatDate(staff.startDate)}
                            </p>
                          </div>
                          <p className="text-sm mt-2 text-black-600 font-medium">
                            Amount: {currencyData[staff?.currency]?.symbol || '$'}{Number(staff.unpaidSalary).toFixed(2)}
                          </p>
                        </div>
                      )}

                      {/* Unpaid Commission */}
                      {staff.unpaidCommission > 0 && (
                        <div className="p-3 bg-red-50 rounded-md">
                          <div className="flex justify-between items-center mb-1">
                            <p className="text-sm text-gray-500">{t("unpaid_commission")}</p>
                            <Badge status="warning" progress="incomplete">
                              Pending
                            </Badge>
                          </div>
                          <div className="flex justify-between items-center mt-2">
                            <div>
                              <p className="text-sm font-medium text-black-600">
                              {currencyData[staff?.currency]?.symbol || '$'}{Number(staff.unpaidCommission).toFixed(2)}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                Total Commission Earned: {currencyData[staff?.currency]?.symbol || '$'}{parseFloat(staff.commission_stats?.total || 0).toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Payment Summary */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                          <p className="text-sm text-gray-500">{t("total_pending_payments")}</p>
                          <p className="text-lg font-semibold text-black-600">
                          {currencyData[staff?.currency]?.symbol || '$'}{(Number(staff.unpaidSalary) + Number(staff.unpaidCommission)).toFixed(2)}
                          </p>
                        </div>
                        <div className="flex flex-col gap-1 mt-2">
                          {staff.unpaidSalary > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">{t("pending_salary")}:</span>
                              <span>{currencyData[staff?.currency]?.symbol || '$'}{Number(staff.unpaidSalary).toFixed(2)}</span>
                            </div>
                          )}
                          {staff.unpaidCommission > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">{t("pending_commission")}:</span>
                              <span>{currencyData[staff?.currency]?.symbol || '$'}{Number(staff.unpaidCommission).toFixed(2)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Layout.AnnotatedSection>

          {/* Payment Details Section */}
          <Layout.AnnotatedSection
            title={t("payment_details_title")}
            description={t("enter_payment_period_type_and_amount_details")}
          >
            <Card sectioned>
              <FormLayout>
                <Select
                  label={t("payment_type_label")}
                  options={[
                    { label: t("salary_payment_type"), value: 'salary' },
                    { label: t("bonus_payment_type"), value: 'bonus' },
                    // { label: t("tip_payment_type"), value: 'tip' }
                  ]}
                  value={payrollData.payment_type}
                  onChange={handleChange('payment_type')}
                  error={error && !payrollData.payment_type ? t("payment_type_required") : ''}
                                      helpText={t("select_payment_type_help")}
                />

                {payrollData.payment_type === 'salary' && (
                  <>
                    <DateRangePicker
                      value={payrollData.dates}
                      onDateRangeSelect={handleDateRangeSelect}
                    />
                    {/* Tell the merchant which dates are selectable BEFORE they
                        try. Earlier dates are already paid; a selection crossing
                        the boundary is clamped rather than rejected. */}
                    {staff?.startDate && staff?.endDate ? (
                      <Text variant="bodySm" as="p" tone="subdued">
                        {t('payable_period_hint', {
                          start: formatPeriodDate(staff.startDate),
                          end: formatPeriodDate(staff.endDate),
                        })}
                      </Text>
                    ) : null}
                    <TextField
                      label={t("salary_amount_label")}
                      type="number"
                      prefix={currencyData[staff?.currency]?.symbol || '$'}
                      // The field, the tax preview below, and handleSave's POST must
                      // all read the SAME state key. This was previously bound for
                      // display to `unpaidSalary` (the full outstanding balance) while
                      // onChange wrote - and handleSave posted - `amount`: typing a
                      // partial payment snapped the display back to the full balance
                      // and silently saved the typed figure instead.
                      value={payrollData.amount !== '' && payrollData.amount !== undefined
                        ? payrollData.amount
                        : (payrollData.unpaidSalary ? Number(payrollData.unpaidSalary).toFixed(2) : '0.00')}
                      onChange={handleChange('amount')}
                      onBlur={handleAmountBlur}
                      error={error && (!payrollData.amount || parseFloat(payrollData.amount) <= 0)
                        ? t("please_enter_a_valid_amount") : ''}
                      helpText={
                        <>
                          <Text variant="bodyMd" as="p" tone="success">
                            Available unpaid salary: {currencyData[staff?.currency]?.symbol || '$'}{staff?.unpaidSalary?.toFixed(2)}
                          </Text>
                          <Text variant="bodySm" as="p" tone="subdued">
                            {staff.payroll_type === 'Hourly'
                              ? `Hourly rate: ${formatCurrency(staff.salary_amount)}/hour` 
                               : staff.payroll_type === 'Weekly' 
                                 ? `Weekly rate: ${formatCurrency(staff.salary_amount)}/week` 
                                  : `Monthly rate: ${formatCurrency(staff.salary_amount)}/month`
                            }
                          </Text>
                        </>
                      }
                    />
                    {renderTaxPreview(payrollData.amount || payrollData.unpaidSalary || 0)}
                  </>
                )}

{payrollData.payment_type === 'commission' && (
  <>
    <Card>
      <BlockStack vertical spacing="loose">
        {/* Header Section */}
        <Box padding="400">
          <BlockStack vertical spacing="400">
            {/* Summary Cards */}
            <BlockStack spacing="300">
              <InlineGrid columns="3" gap="300">
                <Card background="bg-surface-active">
                  <Box padding="300">
                    <BlockStack vertical spacing="200">
                      <Text variant="bodyMd" color="subdued">{t("total_available")}</Text>
                      <Text variant="headingMd" color="success">
                      {currencyData[staff?.currency]?.symbol || '$'}{staff?.unpaidCommission?.toFixed(2)}
                      </Text>
                    </BlockStack>
                  </Box>
                </Card>
                <Card background="bg-surface-active">
                  <Box padding="300">
                    <BlockStack vertical spacing="200">
                      <Text variant="bodyMd" color="subdued">{t("selected_amount")}</Text>
                      <Text variant="headingMd" color="success">
                      {currencyData[staff?.currency]?.symbol || '$'}{selectedCommissions.reduce((sum, id) => {
                          const commission = unpaidCommissions.find(c => c.id === id);
                          return sum + (commission ? parseFloat(commission.commission_earned) : 0);
                        }, 0).toFixed(2)}
                      </Text>
                    </BlockStack>
                  </Box>
                </Card>
                <Card background="bg-surface-active">
                  <Box padding="300">
                    <BlockStack vertical spacing="200">
                      <Text variant="bodyMd" color="subdued">{t("selected_orders")}</Text>
                      <Text variant="headingMd">
                        {selectedCommissions.length} / {unpaidCommissions.length}
                      </Text>
                    </BlockStack>
                  </Box>
                </Card>
              </InlineGrid>
            </BlockStack>
          </BlockStack>
        </Box>

        {/* Commission List with Pagination */}
        <Box padding="400">
          <ResourceList
            resourceName={{ singular: 'commission', plural: 'commissions' }}
            items={unpaidCommissions
              .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
            }
            renderItem={(commission) => {
              const { id, order_id, product_id, commission_earned, earned_at, quantity, original_price } = commission;
              const isSelected = selectedCommissions.includes(id);

              return (
                <ResourceItem
                  id={id}
                  onClick={() => handleCommissionSelection(id)}
                  verticalAlignment="center"
                >
                  <Box padding="300">
                    <BlockStack vertical spacing="300">
                      {/* Order Header */}
                      <BlockStack alignment="center" distribution="equalSpacing">
                        <BlockStack alignment="center" spacing="400">
                          <div onClick={(e) => {
                            // Stop propagation to prevent ResourceItem click
                            e.stopPropagation();
                          }}>
                            <Checkbox
                              checked={isSelected}
                              onChange={() => handleCommissionSelection(id)}
                            />
                          </div>
                          <BlockStack vertical spacing="100">
                            <Text variant="headingSm">{t("order_number", { order_id })}</Text>
                            <Text variant="bodySm" color="subdued">
                              {new Date(earned_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </Text>
                          </BlockStack>
                        </BlockStack>
                        <Badge
                          tone={isSelected ? "success" : "info"}
                          size="large"
                        >
                          {currencyData[staff?.currency]?.symbol || '$'}{Number(commission_earned).toFixed(2)}
                        </Badge>
                      </BlockStack>

                      {/* Order Details */}
                      <Box
                        background="bg-surface-secondary"
                        borderRadius="200"
                        padding="300"
                      >
                        <InlineGrid columns="3" gap="400">
                          <BlockStack vertical spacing="200">
                            <Text variant="bodySm" color="subdued">{t("product_label")}</Text>
                            <Text variant="bodyMd">{product_id.split('/').pop()}</Text>
                          </BlockStack>
                          <BlockStack vertical spacing="200">
                            <Text variant="bodySm" color="subdued">{t("quantity_label")}</Text>
                            <Text variant="bodyMd">{quantity}</Text>
                          </BlockStack>
                          <BlockStack vertical spacing="200">
                            <Text variant="bodySm" color="subdued">{t("original_price_label")}</Text>
                            <Text variant="bodyMd">{currencyData[staff?.currency]?.symbol || '$'}{Number(original_price).toFixed(2)}</Text>
                          </BlockStack>
                        </InlineGrid>
                      </Box>
                    </BlockStack>
                  </Box>
                </ResourceItem>
              );
            }}
          />

          {/* Pagination Controls */}
          {unpaidCommissions.length > itemsPerPage && (
            <Box paddingBlockStart="400">
              <InlineStack align="center" distribute="center">
                <Pagination
                  label={`${((currentPage - 1) * itemsPerPage) + 1}-${Math.min(currentPage * itemsPerPage, unpaidCommissions.length)} of ${unpaidCommissions.length} orders`}
                  hasPrevious={currentPage > 1}
                  onPrevious={() => setCurrentPage(currentPage - 1)}
                  hasNext={currentPage * itemsPerPage < unpaidCommissions.length}
                  onNext={() => setCurrentPage(currentPage + 1)}
                />
              </InlineStack>
            </Box>
          )}
        </Box>

        <TextField
                              label={t("total_commission_amount_label")}
          type="number"
          prefix={currencyData[staff?.currency]?.symbol || '$'}
          value={selectedCommissions.reduce((sum, id) => {
            const commission = unpaidCommissions.find(c => c.id === id);
            return sum + (commission ? parseFloat(commission.commission_earned) : 0);
          }, 0).toFixed(2)}
          autoComplete="off"
          helpText={
            <Text variant="bodySm" color="success">
              {selectedCommissions.length} {t("orders_selected_for_payment")}
            </Text>
          }
        />
      </BlockStack>
    </Card>
  </>
)}

                {payrollData.payment_type === 'bonus' && (
                  <>
                    <TextField
                      label={t("bonus_amount_label")}
                      type="number"
                      prefix={currencyData[staff?.currency]?.symbol || '$'}
                      value={payrollData.amount} // Remove the formatting here
                      onChange={handleChange('amount')}
                      autoComplete="off"
                                              helpText={t("enter_bonus_amount_help")}
                      error={
                        error &&
                          (!payrollData.amount || parseFloat(payrollData.amount) <= 0)
                          ? t("please_enter_a_valid_bonus_amount")
                          : ''
                      }
                    />
                    <TextField
                                              label={t("bonus_reason_label")}
                      value={payrollData.bonus_details || ''}
                      onChange={handleChange('bonus_details')}
                      multiline={3}
                                              placeholder={t("enter_bonus_reason_placeholder")}
                      error={
                        error && !payrollData.bonus_details
                          ? t("please_provide_a_reason_for_the_bonus")
                          : ''
                      }
                    />
                  </>
                )}

                {payrollData.payment_type === 'tip' && (
                  <>
                    <TextField
                                              label={t("tip_amount_label")}
                      type="number"
                      prefix={currencyData[staff?.currency]?.symbol || '$'}
                      value={
                        payrollData.amount
                          ? Number(payrollData.amount).toFixed(2)
                          : '0.00'
                      }
                      onChange={handleChange('amount')}
                      error={
                        error &&
                          (!payrollData.amount ||
                            parseFloat(payrollData.amount) <= 0)
                          ? t("please_enter_a_valid_tip_amount")
                          : ''
                      }
                                              helpText={t("enter_tip_amount_help")}
                    />
                  </>
                )}
              </FormLayout>
            </Card>
          </Layout.AnnotatedSection>

          {/* Payment Method Section */}
          <Layout.AnnotatedSection
            title={t("payment_method_title")}
            description={t("review_and_confirm_staff_payment_details")}
          >
            <Card sectioned>
              <FormLayout>
                <Select
                  label={t("payment_method_label")}
                  options={[
                    { label: t("paypal_payment_method"), value: 'PayPal' },
                    { label: t("stripe_payment_method"), value: 'Stripe' },
                    { label: 'Wise (TransferWise)', value: 'Wise' },
                    { label: 'Payoneer', value: 'Payoneer' },
                    { label: t("bank_transfer_payment_method"), value: 'Bank Transfer' },
                    { label: 'Direct Deposit', value: 'Direct Deposit' },
                    { label: 'Cash', value: 'Cash' },
                    { label: 'Check', value: 'Check' },
                    { label: 'Revolut', value: 'Revolut' },
                    { label: 'Venmo', value: 'Venmo' },
                    { label: 'Square Cash', value: 'Square' }
                  ]}
                  value={payrollData.payment_method || staff.staff_data.payment_method}
                  onChange={handleChange('payment_method')}
                  error={error && !payrollData.payment_method ? t("payment_method_required") : ''}
                />

                {/* Display Payment Details based on selected method */}
                {(payrollData.payment_method || staff.staff_data.payment_method) && (
  <Card>
    <Box padding="400">
      <BlockStack vertical spacing="400">
        {/* Header */}
        <Box
          paddingBlockEnd="400"
          borderBlockEndWidth="025"
          borderColor="border"
        >
          <InlineStack align="space-between">
            <BlockStack vertical spacing="100">
              <Text variant="headingMd" as="h3">{t("staff_payment_details_title")}</Text>
              <Text variant="bodySm" color="subdued">
                {t("staff_payment_details_description")}
              </Text>
            </BlockStack>
            <Badge tone="success">{payrollData.payment_method || staff.staff_data.payment_method}</Badge>
          </InlineStack>
        </Box>

            {/* Payment Method Icon and Summary */}
            <Box padding="400" background="bg-surface-secondary" borderRadius="200">
          <InlineStack align="start" gap="400">
            <div style={{ 
              width: '48px', 
              height: '48px',
              borderRadius: '12px',
              background: 'var(--p-color-bg-surface-strong)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Icon
              source={getPaymentMethodIcon(payrollData.payment_method || staff.staff_data.payment_method)}
              color="primary"
              tone="strong"
            /> 
            </div>
            <BlockStack vertical spacing="100">
              <Text variant="headingSm">
                {getPaymentMethodTitle(payrollData.payment_method || staff.staff_data.payment_method)}
              </Text>
              <Text variant="bodySm" color="subdued">
                {getPaymentMethodDescription(payrollData.payment_method || staff.staff_data.payment_method)}
              </Text>
            </BlockStack>
          </InlineStack>
        </Box>

                       {/* Payment Details Content */}
        <Box paddingBlockStart="400">
          <Card>
            <Box padding="400" background="bg-surface-secondary" borderRadius="200">
              {/* PayPal Details */}
              {(payrollData.payment_method || staff.staff_data.payment_method) === 'PayPal' && (
                <PaymentDetailsSection
                  title={t("paypal_account_information_title")}
                  fields={[
                    {
                                              label: t("email_address_label"),
                      value: staff.staff_data.payment_email,
                      icon: EmailIcon
                    },
                    {
                      label: t("account_name_label"),
                      value: staff.staff_data.paypal_name,
                      icon: ProfileIcon
                    }
                  ]}
                />
              )}
                       
                        {/* Bank Transfer Details */}
                        {(payrollData.payment_method || staff.staff_data.payment_method) === 'Bank Transfer' && (
                <PaymentDetailsSection
                  title={t("bank_account_information_title")}
                  fields={[
                    {
                      label: t("bank_name_label"),
                      value: staff.staff_data.bank_name,
                      icon: BankIcon
                    },
                    {
                                              label: t("account_type_label"),
                      value: staff.staff_data.bank_account_type,
                      badge: true
                    },
                    {
                      label: t("account_holder_label"),
                      value: staff.staff_data.account_holder_name,
                      icon: ProfileIcon
                    },
                    {
                      label: t("account_number_label"),
                      value: staff.staff_data.account_number,
                      secured: true
                    },
                    ...(staff.staff_data.bank_account_type === 'domestic' 
                      ? [{
                          label: t("routing_number_label"),
                          value: staff.staff_data.routing_number,
                          secured: true
                        }]
                      : [
                          {
                            label: "SWIFT/BIC",
                            value: staff.staff_data.swift_code,
                            secured: true
                          },
                          {
                            label: t("iban_label"),
                            value: staff.staff_data.iban,
                            secured: true
                          }
                        ])
                  ]}
                />
              )}



{/* Stripe Details */}
{(payrollData.payment_method || staff.staff_data.payment_method) === 'Stripe' && (
  <PaymentDetailsSection
    title={t("stripe_account_information_title")}
    fields={[
      {
        label: "Stripe Email",
        value: staff.staff_data.stripe_email,
        icon: EmailIcon,
      },
      {
        label: "Account ID",
        value: staff.staff_data.stripe_account_id,
        icon: PaymentIcon,
        secured: true
      }
    ]}
  />
)}

{/* Wise Details */}
{(payrollData.payment_method || staff.staff_data.payment_method) === 'Wise' && (
  <PaymentDetailsSection
    title="Wise Transfer Details"
    fields={[
      {
        label: "Wise Email",
        value: staff.staff_data.wise_email,
        icon: EmailIcon
      },
      {
        label: "Account Name",
        value: staff.staff_data.wise_account_name,
        icon: ProfileIcon
      },
      {
        label: "Currency",
        value: staff.staff_data.wise_currency,
        icon: PaymentIcon,
        badge: true
      }
    ]}
  />
)}

{/* Direct Deposit Details */}
{(payrollData.payment_method || staff.staff_data.payment_method) === 'Direct Deposit' && (
  <PaymentDetailsSection
    title="Direct Deposit Information"
    fields={[
      {
        label: "Bank Name",
        value: staff.staff_data.bank_name,
        icon: BankIcon
      },
      {
        label: "Account Holder",
        value: staff.staff_data.account_holder_name,
        icon: ProfileIcon
      },
      {
        label: "Account Number",
        value: staff.staff_data.account_number,
        icon: PaymentIcon,
        secured: true
      },
      {
        label: "Routing Number",
        value: staff.staff_data.routing_number,
        icon: PaymentIcon,
        secured: true
      }
    ]}
  />
)}

{/* Revolut Details */}
{(payrollData.payment_method || staff.staff_data.payment_method) === 'Revolut' && (
  <PaymentDetailsSection
    title="Revolut Account Details"
    fields={[
      {
        label: "Email/Phone",
        value: staff.staff_data.revolut_contact,
        icon: EmailIcon
      },
      {
        label: "Username",
        value: staff.staff_data.revolut_username,
        icon: ProfileIcon
      }
    ]}
  />
)}

{/* Venmo Details */}
{(payrollData.payment_method || staff.staff_data.payment_method) === 'Venmo' && (
  <PaymentDetailsSection
    title="Venmo Account Details"
    fields={[
      {
        label: "Username",
        value: staff.staff_data.venmo_username,
        icon: ProfileIcon
      },
      {
        label: "Phone Number",
        value: staff.staff_data.venmo_phone,
        icon: EmailIcon
      }
    ]}
  />
)}

{/* Square Cash Details */}
{(payrollData.payment_method || staff.staff_data.payment_method) === 'Square' && (
  <PaymentDetailsSection
    title="Square Cash Account"
    fields={[
      {
        label: "$Cashtag",
        value: staff.staff_data.cash_tag,
        icon: PaymentIcon
      },
      {
        label: "Phone/Email",
        value: staff.staff_data.cash_app_contact,
        icon: EmailIcon
      }
    ]}
  />
)}

{/* Cash Payment Details */}
{(payrollData.payment_method || staff.staff_data.payment_method) === 'Cash' && (
  <PaymentDetailsSection
    title="Cash Payment"
    fields={[
      {
        label: "Payment Method",
        value: "Cash Payment",
        icon: PaymentIcon,
        badge: true
      },
      {
        label: "Note",
        value: "Please ensure to collect a signed receipt",
        icon: ProfileIcon
      }
    ]}
  />
)}

{/* Check Payment Details */}
{(payrollData.payment_method || staff.staff_data.payment_method) === 'Check' && (
  <PaymentDetailsSection
    title={t("check_payment_details")}
    fields={[
      {
        label: t("payee_name"),
        value: `${staff.staff_data.first_name} ${staff.staff_data.last_name}`,
        icon: ProfileIcon
      },
      {
        label: t("payment_method"),
        value: "Check Payment",
        icon: PaymentIcon,
        badge: true
      },
      {
        label: t("note"),
        value: "Please ensure the check is properly filled out",
        icon: ProfileIcon
      }
    ]}
  />
)}
                      </Box>
          </Card>
        </Box>
        </BlockStack>

                      {/* Warning if payment details are incomplete */}
                      {!isPaymentDetailsComplete(staff.staff_data, payrollData.payment_method || staff.staff_data.payment_method) && (
          <Box paddingBlockStart="400">
            <Banner
              title={t("incomplete_payment_details")}
              tone="warning"
              action={{
                content: t("update_staff_profile"),
                onAction: () => navigate(`/StaffManagement/editStaff/${staff.id}`)
              }}
            >
              <Text>{t("some_required_payment_details_are_missing_please_update_the_staff_profile")}</Text>
            </Banner>
          </Box>
        )}
                    </Box>
                  </Card>
                )}

                <TextField
                  label={t("additional_notes")}
                  value={payrollData.notes}
                  onChange={handleChange('notes')}
                  multiline={3}
                  placeholder={t("add_any_payment_notes_here")}
                />
              </FormLayout>
            </Card>
          </Layout.AnnotatedSection>

          {/* Payment Proof Section */}
          <Layout.AnnotatedSection
            title={t("payment_proof")}
            description={t("attach_proof_of_payment")}
          >
            <Card sectioned>
              <FormLayout>
                <div style={{ marginBottom: '16px' }}>
                  <Text variant="bodyMd" as="p" color="subdued">
                    {t("accepted_file_types")}: PDF, PNG, JPG, JPEG (Max size: 5MB)
                  </Text>
                </div>
                {payrollData.payment_proof ? (
                  <div style={{ marginBottom: '16px' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <div
                        style={{
                          padding: '8px',
                          border: '1px solid #dde0e4',
                          borderRadius: '4px',
                          flex: 1
                        }}
                      >
                        <Text variant="bodyMd">
                          {typeof payrollData.payment_proof === 'string'
                            ? payrollData.payment_proof.split('/').pop()
                            : payrollData.payment_proof.name}
                        </Text>
                      </div>
                      <Button
                        plain
                        destructive
                        onClick={() => {
                          setPayrollData(prev => ({
                            ...prev,
                            payment_proof: null
                          }));
                          setIsDirty(true);
                        }}
                      >
                        {t("remove")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      border: '2px dashed #dde0e4',
                      borderRadius: '4px',
                      padding: '16px',
                      textAlign: 'center',
                      cursor: 'pointer'
                    }}
                    onClick={() => document.getElementById('payment-proof-input').click()}
                  >
                    <input
                      id="payment-proof-input"
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          if (file.size > 5 * 1024 * 1024) {
                            setError('File size should not exceed 5MB');
                            return;
                          }
                          setPayrollData(prev => ({
                            ...prev,
                            payment_proof: file
                          }));
                          setIsDirty(true);
                        }
                      }}
                    />
                    <div style={{ marginBottom: '8px' }}>
                      <Text variant="bodyMd" as="p">
                          {t("drop_your_file_here_or_click_to_upload")}
                      </Text>
                    </div>
                    <Button>{t("choose_file")}</Button>
                  </div>
                )}
                {error && error.includes(t("file_size")) && (
                  <div style={{ marginTop: '4px', color: 'rgb(220, 38, 38)' }}>
                    <Text variant="bodyMd" as="p" color="critical">
                      {error}
                    </Text>
                  </div>
                )}
              </FormLayout>
            </Card>
          </Layout.AnnotatedSection>
        </Layout>
      </Form>

      {showToast && (
        <Frame>
        <Toast
          content={toastContent}
          onDismiss={() => setShowToast(false)}
          duration={3000}
        />
        </Frame>
      )}
    </Page>
    </>
  );
}


const PaymentDetailsSection = ({ title, fields }) => {
  /* 1️⃣  Detect current viewport */
  const { smDown } = useBreakpoints();       // true ≈ ≤ 640 px
  const isPhone = smDown;

  return (
    <div className="max-w-full space-y-4">
      {/* Section heading ----------------------------------------------- */}
      <h4 className="text-base font-semibold">{title}</h4>

      {/* Card body ------------------------------------------------------ */}
      <div
        className={`w-full rounded-md border border-gray-200 bg-white ${
          isPhone ? 'p-3' : 'p-4'
        }`}
      >
        <div className="space-y-3">
          {fields.map((field, idx) => {
            /* pieces reused in both layouts --------------------------- */
            const LabelBlock = (
              <div className="flex flex-wrap items-center gap-2">
                {field.icon && (
                  <span className="text-gray-500">{field.icon}</span>
                )}
                <span className="text-gray-500">{field.label}:</span>
              </div>
            );

            const ValueBlock = field.secured ? (
              <span style={{ letterSpacing: '1px' }}>{field.value}</span>
            ) : field.badge ? (
              <span className="inline-block rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                {field.value}
              </span>
            ) : (
              <span>{field.value || 'Not set'}</span>
            );

            /* 2️⃣ Phone ⇢ stacked • Tablet+ ⇢ label left / value right */
            return isPhone ? (
              <div key={idx} className="flex flex-col gap-1 sm:flex-row sm:gap-0">
                {LabelBlock}
                {ValueBlock}
              </div>
            ) : (
              <div
                key={idx}
                className="flex flex-wrap items-center justify-between gap-2"
              >
                {LabelBlock}
                {ValueBlock}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------
   Utility helpers (unchanged)
------------------------------------------------------------------ */
const getPaymentMethodIcon = (method) => {
  switch (method) {
    case 'PayPal':
      return PaymentIcon;
    case 'Bank Transfer':
    case 'Direct Deposit':
      return BankIcon;
    default:
      return PaymentIcon;
  }
};

const getPaymentMethodTitle = (method) => {
  switch (method) {
    case 'PayPal':
      return 'PayPal Account';
    case 'Bank Transfer':
      return 'Bank Transfer Details';
    case 'Direct Deposit':
      return 'Direct Deposit Information';
    case 'Cash':
      return 'Cash Payment';
    default:
      return 'Payment Information';
  }
};

const getPaymentMethodDescription = (method) => {
  switch (method) {
    case 'PayPal':
      return 'Payments will be processed through PayPal';
    case 'Bank Transfer':
      return 'Direct bank transfer to staff account';
    case 'Direct Deposit':
      return 'Automated bank deposit to staff account';
    case 'Cash':
      return 'Physical cash payment to staff';
    default:
      return 'Payment method details';
  }
};